import { Alert, Button, Form, message, Switch } from 'antd'
import SettingsCard from 'egi/app/Settings/components/SettignsCard/SettingsCard'
import PartnerApiRepository from 'egi/repositories/PartnerApiRepository'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import swal from 'utils/swal'
import { validateResponse } from 'utils/validate'
import { IPartnerDetail, PartnerUpdateBody } from '../partnerApiCredentials'
import SecretInput from './SecretInput'

interface IProps {
  promoter: IPartnerDetail
  onSave: (body: PartnerUpdateBody) => Promise<void>
  onReload: () => Promise<void>
  onOpenHistory: () => void
}

const GENERATED_TOKEN_FIELD_ID = 'partner-generated-token'

function showGeneratedToken (token: string) {
  let field: HTMLElement | null = null

  swal.htmlBasic({
    icon: 'success',
    title: 'Token gerado',
    text: `<p>Copie e envie ao parceiro.</p><p class="mt-3 mb-1"><b>Novo token Bearer</b></p><div id="${GENERATED_TOKEN_FIELD_ID}"></div>`,
    force: true,
    didOpen: () => {
      field = document.getElementById(GENERATED_TOKEN_FIELD_ID)
      if (field) ReactDOM.render(<SecretInput value={token} readOnly defaultRevealed />, field)
    },
    didClose: () => {
      if (field) ReactDOM.unmountComponentAtNode(field)
      field = null
    }
  })
}

export default function PartnerTokenSection ({ promoter, onSave, onReload, onOpenHistory }: IProps) {
  const [token, setToken] = useState(promoter.token ?? '')
  const [enabled, setEnabled] = useState(Boolean(promoter.partner))
  const [editing, setEditing] = useState(false)
  const [tokenError, setTokenError] = useState<string>()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setToken(promoter.token ?? '')
    setEnabled(Boolean(promoter.partner))
    setEditing(false)
    setTokenError(undefined)
  }, [promoter.token, promoter.partner])

  const hasToken = Boolean(token.trim())

  const generateToken = async () => {
    try {
      const response = await PartnerApiRepository.generateToken(promoter._id)
      const newToken = response.data.data?.token
      if (newToken) showGeneratedToken(newToken)
      await onReload()
    } catch (err) {
      message.error(err.message)
    }
  }

  const confirmGenerateToken = () => {
    swal.confirmNegate({
      icon: 'warning',
      title: 'Trocar o token?',
      text: 'O token atual deixa de funcionar imediatamente. O parceiro precisará atualizar a integração com o novo token.',
      confirmButtonText: 'Gerar',
      confirm: generateToken
    })
  }

  const save = async () => {
    setSaving(true)
    setTokenError(undefined)

    const body: PartnerUpdateBody = { partner: enabled }
    if (token.trim() && token !== (promoter.token ?? '')) body.token = token

    try {
      await onSave(body)
    } catch (err) {
      setTokenError(validateResponse(err.data?.invalid).token ?? err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsCard className="mb-2">
      <div className="flex flex-justify-between">
        <div>
          <SettingsCard.title>Parceiro de API</SettingsCard.title>
          <SettingsCard.description>
            Habilite o acesso do promotor e gerencie o token Bearer que ele usa na API da AGX.
          </SettingsCard.description>
        </div>
        <div className="ml-3">
          <Button type="primary" onClick={onOpenHistory}>Histórico</Button>
        </div>
      </div>

      {promoter.canceled && (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message="Este promotor está cancelado e não pode ser habilitado como parceiro."
        />
      )}

      {promoter.blocked && (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message="Este promotor está bloqueado; as chamadas de API são recusadas mesmo com o parceiro habilitado."
        />
      )}

      <Form layout="vertical" className="mt-3" onFinish={save}>
        <h4 className="font-500">Token de acesso</h4>

        <Form.Item
          label="Token Bearer"
          validateStatus={tokenError ? 'error' : undefined}
          help={tokenError ?? (hasToken ? 'Usado pelo parceiro no header Authorization' : 'Nenhum token cadastrado')}
        >
          <SecretInput
            value={token}
            readOnly={!editing && hasToken}
            concealed
            onChange={setToken}
            onEdit={() => {
              setEditing(true)
              setToken('')
            }}
            editing={editing}
            onCancel={() => {
              setEditing(false)
              setToken(promoter.token ?? '')
              setTokenError(undefined)
            }}
          />
        </Form.Item>

        <Button type={hasToken ? 'ghost' : 'primary'} onClick={confirmGenerateToken}>
          Gerar novo token
        </Button>

        <div className="mt-3 mb-3">
          <Switch checked={enabled} disabled={(!hasToken && !enabled) || (Boolean(promoter.canceled) && !enabled)} onChange={setEnabled} />
          <span className="ml-2">Parceiro de API habilitado</span>
          <p className="font-size-12 color-gray mt-2 mb-0">
            {hasToken
              ? 'Ao desabilitar, as chamadas com o token deste promotor passam a ser recusadas'
              : 'Gere ou informe um token para habilitar este parceiro. Sem token, as chamadas não funcionam.'}
          </p>
        </div>

        <SettingsCard.ButtonsContainer>
          <SettingsCard.submitButton loading={saving} disabled={!hasToken && (enabled || enabled === Boolean(promoter.partner))}>
            Salvar
          </SettingsCard.submitButton>
        </SettingsCard.ButtonsContainer>
      </Form>
    </SettingsCard>
  )
}
